import {
  CircularProgress,
  Typography
} from "@mui/material";
import { Box } from "@mui/system";
import { BarChart } from "@mui/x-charts/BarChart";
import { useMemo } from "react";

import { StackBarChart } from "../../types/leads.types";

interface Props {
  data?: StackBarChart | null;
  isLoading?: boolean;
  title?: string;
  color?: string;
  hasError?: boolean;
}

export default function LeadsStackBarChart({
  data,
  isLoading = false,
  title = "Chart",
  hasError = false
}: Props) {
  const xLabels = useMemo(() => {
    if (!data) return [];
    return data.xLabels;
  }, [data]);

  const seriesData = useMemo(() => {
    if (!data) return [];
    return data.series.map((series, index) => ({
      ...series,
      color: index === 0 ? "#E87722" : index === 1 ? "#febc2c" : "#0097a9" // Orange for Hot, Yellow for Warm, Teal for Cold
    }));
  }, [data]);


  return (
    <Box sx={{
      width: "100%",
      height: "450px",
      border: "solid 1px #0000001F",
      borderRadius: "24px",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden"
    }}>
      <Box sx={{ padding: "16px 16px 0 16px" }}>
        <Typography variant="title_medium">{title}</Typography>
      </Box>
      {
        isLoading ? (<Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <CircularProgress />
        </Box>) : hasError ? (<Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Typography color="text.secondary">
            Error in displaying graph data
          </Typography>
        </Box>) : !data || Object.keys(data).length === 0 ? (
          <Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Typography color="text.secondary">
              No data available
            </Typography>
          </Box>
        ) : (
          <Box sx={{
            flex: 1,
            width: "100%",
            minHeight: 0
          }}>
            <Box sx={{
              width: "100%", // Always use full width
              minWidth: "300px"
            }}>
              <BarChart
                series={seriesData}
                xAxis={[{
                  data: xLabels,
                  scaleType: "band",
                  tickLabelStyle: {
                    angle: xLabels.length > 4 ? -45 : 0, // Only angle labels if there are many items
                    textAnchor: xLabels.length > 4 ? "end" : "middle",
                    fontSize: 12,
                    fill: "#666",
                  }
                }]}
                height={380}
                slotProps={{
                  legend: {
                    direction: "row",
                    position: { vertical: "top", horizontal: "middle" },
                    padding: 20,
                    itemMarkWidth: 18,
                    itemMarkHeight: 18,
                    markGap: 10,
                    itemGap: 25,
                  },
                }}
                margin={{
                  left: 60,
                  right: 60,
                  top: 60, // More space for legend
                  bottom: xLabels.length > 4 ? 100 : 70 // More bottom margin for angled labels
                }}
                sx={{
                  width: "100%",
                  "& .MuiChartsAxis-tickLabel": {
                    fontSize: "0.8rem",
                    fill: "#666",
                  },
                  "& .MuiChartsAxis-line": {
                    stroke: "#e0e0e0",
                  },
                  "& .MuiChartsGrid-line": {
                    stroke: "#f0f0f0",
                  },
                  "& .MuiChartsLegend-root": {
                    transform: "translateY(-10px)", // Slight adjustment to prevent overlap
                    "& .MuiChartsLegend-series": {
                      fontSize: "0.875rem",
                      marginRight: "25px !important", // Add space between legend items
                      "& text": {
                        fill: "#333 !important",
                        fontWeight: 500,
                      },
                      "& rect": {
                        marginRight: "10px !important", // Add space between color square and text
                      }
                    },
                    // Target individual legend items for better spacing
                    "& g[class*='MuiChartsLegend-series']": {
                      "& rect + text": {
                        transform: "translateX(5px)", // Move text further from the color square
                      }
                    }
                  },
                  // Make bars slimmer and center them
                  // "& .MuiBarElement-root": {
                  //   // width: xLabels.length === 1 ? "60px !important" : "",
                  //   // Override the transform to center the bar horizontally
                  //   // ...(xLabels.length === 1 && {
                  //   //   transform: "translate3d(calc(50% - 30px), 100px, 0px) !important",
                  //   // }),
                  // },
                  // Ensure graph expands to full width
                  "& .MuiResponsiveChart-container": {
                    width: "100%",
                    height: "100%",
                    "& svg": {
                      width: "100%",
                      height: "100%",
                      display: "block",
                    }
                  }
                }}
              />
            </Box>
          </Box>

        )
      }

    </Box>
  )
}